/*
PURPOSE: Reusable button component for resonating with comments

USE CASES:
- Primary resonate action in CommentCard for top-level comments
- Secondary resonate action for nested replies
- Displaying resonance counts alongside the icon

DOMAIN BOUNDARIES:
- Handles visual state and user interaction for resonance
- Integrates with ResonateIcon for consistent visual feedback
- Should NOT handle API calls or state management

ASSUMPTIONS:
- Coral pink color scheme for top-level resonance
- Purple color scheme for nested comment resonance
- Size variants match the comment hierarchy visual design
*/

import React from 'react';
import ResonateIcon from '../ResonateIcon/ResonateIcon';

interface ResonateButtonProps {
  /** Whether the button is in a resonated state */
  isResonated: boolean;
  /** Click handler */
  onClick: () => void;
  /** Whether to show the "Resonate" text */
  showText?: boolean;
  /** Size variant */
  size?: 'large' | 'primary' | 'secondary';
  /** Whether this is for nested comments (affects color scheme) */
  isNested?: boolean;
  /** Number to display (typically resonance count) */
  count?: number;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show activation animation */
  showActivationPop?: boolean;
  /** Custom aria-label */
  ariaLabel?: string;
  /** Test ID attributes */
  testId?: string;
  commentId?: string;
}

const ResonateButton: React.FC<ResonateButtonProps> = ({
  isResonated,
  onClick,
  showText = false,
  size = 'primary',
  isNested = false,
  count,
  disabled = false,
  className = '',
  showActivationPop = false,
  ariaLabel,
  testId = 'resonate-button',
  commentId
}) => {
  // Size configurations
  const sizeConfig = {
    large: {
      iconSize: 24,
      textSize: 'text-base',
      textWeight: 'font-normal',
      gap: 'gap-2'
    },
    primary: {
      iconSize: 19,
      textSize: 'text-sm',
      textWeight: 'font-extralight',
      gap: 'gap-1'
    },
    secondary: {
      iconSize: 17,
      textSize: 'text-xs',
      textWeight: 'font-extralight',
      gap: 'gap-0.5'
    }
  };

  // Color schemes for different states
  const colorSchemes = {
    // For top-level elements
    primary: {
      default: 'text-gray-400/50 hover:text-coral-pink',
      resonated: 'text-coral-pink',
      countColor: 'text-coral-pink'
    },
    // For nested elements - using purple colors
    secondary: {
      default: 'text-gray-400/50 hover:text-coral-pink',
      resonated: 'text-purple-500',
      countColor: 'text-purple-500'
    }
  };

  const colorScheme = isNested ? colorSchemes.secondary : colorSchemes.primary;
  const config = sizeConfig[size];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group inline-flex items-center ${config.gap} transition-colors duration-200 ${
        isResonated ? colorScheme.resonated : colorScheme.default
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
      style={{ marginTop: isResonated && showText ? '1px' : '0' }}
      aria-label={ariaLabel || (isResonated ? "Remove resonance" : "Resonate")}
      data-testid={testId}
      data-comment-id={commentId}
    >
      <ResonateIcon 
        isResonated={isResonated}
        isNested={isNested}
        size={config.iconSize}
        iconType="intersection"
        showActivationPop={showActivationPop}
      />
      {showText && !isResonated && (
        <span className={`${config.textSize} ${config.textWeight} ml-1 transition-colors duration-200 leading-none`}>
          Resonate
        </span>
      )}
      {(size === 'large' || size === 'primary') && count !== undefined && count > 0 && (
        <span className={`${size === 'large' ? 'text-sm' : 'text-xs'} font-medium ${isResonated ? colorScheme.countColor : ''} ml-1 leading-none`}>
          {count}
        </span>
      )}
    </button>
  );
};

export default ResonateButton;